import { useEffect, useMemo, useState } from "react";
import { Layout } from "../../components/layout/Layout";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { ALL_GRADES } from "../../constants";
import { BadgeScanInput } from "../../components/BadgeScanInput";
import {
  Plus, Pencil, Trash2, Shield, Search, Package, ShoppingCart, Contact, FileText, Wrench,
  Stethoscope, RefreshCcw, ListChecks, TrendingUp, Warehouse, MessageSquare, Building2, Share2,
  Check, X as XIcon, LockKeyhole, LockOpen, Users, CalendarCheck,
} from "lucide-react";
import { toast } from "sonner";

// Correspond aux "icon" du catalogue renvoyé par GET /admin/permissions/catalog
// (backend/constants.py > PERMISSIONS_CATALOG).
const ICONS = {
  Package, ShoppingCart, Contact, FileText, Wrench, Stethoscope, RefreshCcw,
  ListChecks, TrendingUp, Warehouse, MessageSquare, Building2, Share2, Users, CalendarCheck,
};

function isModuleFullyGranted(mod, permTarget) {
  const val = (permTarget.permissions || {})[mod.id];
  if (mod.type === "boolean") return !!val;
  if (!val) return false;
  return mod.actions.every((a) => !!val[a.key]);
}

function isModuleAnyGranted(mod, permTarget) {
  const val = (permTarget.permissions || {})[mod.id];
  if (mod.type === "boolean") return !!val;
  if (!val) return false;
  return mod.actions.some((a) => !!val[a.key]);
}

export default function UsersPage({ embedded = false }) {
  const [users, setUsers] = useState([]);
  const [shops, setShops] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [open, setOpen] = useState(false);
  const [permOpen, setPermOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nom: "", prenom: "", poste: "", grades: [], shop_id: "", telephone: "", email: "", matricule: "", pin: "", commission_percent: "", badge_type: "", badge_code: "" });
  const [permTarget, setPermTarget] = useState(null);
  const [permSearch, setPermSearch] = useState("");

  const load = async () => {
    const [u, s] = await Promise.all([api.get("/users"), api.get("/shops")]);
    setUsers(u.data);
    setShops(s.data);
  };
  useEffect(() => {
    load();
    api.get("/admin/permissions/catalog").then((r) => setCatalog(r.data.catalog)).catch(() => {});
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ nom: "", prenom: "", poste: "", grades: [], shop_id: shops[0]?.id || "", telephone: "", email: "", matricule: "", pin: "", commission_percent: "", badge_type: "", badge_code: "" });
    setOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ nom: u.nom, prenom: u.prenom, poste: u.poste, grades: u.grades || [], shop_id: u.shop_id || "", telephone: u.telephone || "", email: u.email || "", matricule: u.matricule || "", pin: "", commission_percent: u.commission_percent ?? "", badge_type: u.badge_type || "", badge_code: u.badge_code || "" });
    setOpen(true);
  };

  const toggleGrade = (g) => {
    setForm((f) => ({ ...f, grades: f.grades.includes(g) ? f.grades.filter((x) => x !== g) : [...f.grades, g] }));
  };

  const save = async () => {
    if (!form.shop_id) {
      toast.error("Merci de sélectionner une boutique / dépôt d'affectation");
      return;
    }
    if (form.matricule.length !== 7) {
      toast.error("Le matricule doit contenir exactement 7 chiffres");
      return;
    }
    if (!editing && form.pin.length !== 8) {
      toast.error("Le code PIN doit contenir exactement 8 chiffres");
      return;
    }
    if (editing && form.pin && form.pin.length !== 8) {
      toast.error("Le code PIN doit contenir exactement 8 chiffres");
      return;
    }
    if (form.badge_type && !form.badge_code.trim()) {
      toast.error("Passez le badge ou saisissez son code, ou repassez le type de badge à \"Aucun\"");
      return;
    }
    try {
      const payload = {
        ...form,
        commission_percent: form.commission_percent === "" ? null : Number(form.commission_percent),
      };
      if (editing) {
        if (!payload.pin) delete payload.pin;
        await api.patch(`/users/${editing.id}`, payload);
      } else {
        await api.post("/users", payload);
      }
      toast.success("Utilisateur enregistré");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      toast.success("Utilisateur supprimé");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { active: u.active === false ? true : false });
      toast.success(u.active === false ? "Compte réactivé" : "Compte désactivé temporairement");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const isLocked = (u) => !!u.locked_until && new Date(u.locked_until).getTime() > Date.now();

  const unlock = async (id) => {
    try {
      await api.post(`/users/${id}/unlock`);
      toast.success("Compte débloqué");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openPerms = (u) => { setPermTarget(u); setPermSearch(""); setPermOpen(true); };

  const togglePerm = (module, action) => {
    setPermTarget((t) => {
      const perms = { ...t.permissions };
      if (typeof perms[module] === "boolean") {
        perms[module] = !perms[module];
      } else {
        perms[module] = { ...(perms[module] || {}), [action]: !(perms[module] || {})[action] };
      }
      return { ...t, permissions: perms };
    });
  };

  const setModuleAll = (mod, value) => {
    setPermTarget((t) => {
      const perms = { ...t.permissions };
      if (mod.type === "boolean") {
        perms[mod.id] = value;
      } else {
        perms[mod.id] = Object.fromEntries(mod.actions.map((a) => [a.key, value]));
      }
      return { ...t, permissions: perms };
    });
  };

  const savePerms = async () => {
    try {
      await api.patch(`/users/${permTarget.id}`, { permissions: permTarget.permissions });
      toast.success("Permissions mises à jour");
      setPermOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const recalculerPermissions = async () => {
    if (!window.confirm(
      "Réinitialiser les permissions de cette personne aux droits exacts de son ou ses grade(s) actuel(s) ? " +
      "Toute permission ajoutée manuellement en plus d'un grade sera perdue (à réappliquer ensuite si voulue)."
    )) return;
    try {
      const { data } = await api.post(`/users/${permTarget.id}/recalculer-permissions`);
      setPermTarget(data);
      toast.success("Permissions réinitialisées aux grades actuels");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const filteredCatalog = useMemo(() => {
    const q = permSearch.trim().toLowerCase();
    if (!q) return catalog;
    return catalog
      .map((mod) => {
        if (mod.label.toLowerCase().includes(q)) return mod;
        if (mod.type === "boolean") return mod.boolean_label?.toLowerCase().includes(q) ? mod : null;
        const actions = mod.actions.filter((a) => a.label.toLowerCase().includes(q));
        return actions.length ? { ...mod, actions } : null;
      })
      .filter(Boolean);
  }, [catalog, permSearch]);

  const body = (
    <>
      <div className="flex justify-end mb-5">
        <Button data-testid="user-add-button" onClick={openCreate} className="gap-2"><Plus size={16} />Nouvel utilisateur</Button>
      </div>
      <div className="rounded-xl border border-border bg-card overflow-x-auto" data-testid="user-list">
        <table className="w-full text-sm min-w-[600px]">
          <thead className="bg-secondary text-left">
            <tr><th className="p-3">Nom</th><th className="p-3">Matricule</th><th className="p-3">Email</th><th className="p-3">Poste</th><th className="p-3">Grades</th><th className="p-3">Boutique</th><th className="p-3">Statut</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className={`border-t border-border ${u.active === false ? "opacity-60" : ""}`} data-testid={`user-row-${u.id}`}>
                <td className="p-3">{u.prenom} {u.nom}</td>
                <td className="p-3 font-mono text-xs">{u.matricule || "-"}</td>
                <td className="p-3 text-xs text-muted-foreground">{u.email || "-"}</td>
                <td className="p-3">{u.poste}</td>
                <td className="p-3">{(u.grades || []).join(", ")}</td>
                <td className="p-3">{shops.find((s) => s.id === u.shop_id)?.nom || "-"}</td>
                <td className="p-3">
                  {!u.is_admin ? (
                    <button
                      onClick={() => toggleActive(u)}
                      data-testid={`user-toggle-active-${u.id}`}
                      className={`text-xs px-2 py-1 rounded-md border ${u.active === false ? "border-destructive/40 text-destructive" : "border-emerald-500/40 text-emerald-600"}`}
                      title={u.active === false ? "Cliquer pour réactiver ce compte" : "Cliquer pour désactiver temporairement ce compte (arrêt, congé...)"}
                    >
                      {u.active === false ? "Inactif" : "Actif"}
                    </button>
                  ) : (
                    <span className="text-xs text-muted-foreground">Actif</span>
                  )}
                </td>
                <td className="p-3 flex justify-end gap-2">
                  {isLocked(u) && (
                    <button
                      data-testid={`user-unlock-${u.id}`}
                      onClick={() => unlock(u.id)}
                      title={`Compte bloqué jusqu'à ${new Date(u.locked_until).toLocaleString("fr-FR")} — cliquer pour débloquer`}
                      className="p-1.5 rounded-md hover:bg-destructive/10 text-destructive"
                    >
                      <LockKeyhole size={15} />
                    </button>
                  )}
                  <button data-testid={`user-perms-${u.id}`} onClick={() => openPerms(u)} title="Permissions" className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground"><Shield size={15} /></button>
                  <button data-testid={`user-edit-${u.id}`} onClick={() => openEdit(u)} className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground"><Pencil size={15} /></button>
                  {!u.is_admin && <button data-testid={`user-delete-${u.id}`} onClick={() => remove(u.id)} className="p-1.5 rounded-md hover:bg-destructive/10 text-destructive"><Trash2 size={15} /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-form-dialog" className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Modifier l'utilisateur" : "Nouvel utilisateur"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input data-testid="user-form-nom" placeholder="Nom" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} />
              <Input data-testid="user-form-prenom" placeholder="Prénom" value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} />
            </div>
            <Input data-testid="user-form-poste" placeholder="Poste" value={form.poste} onChange={(e) => setForm({ ...form, poste: e.target.value })} />
            <Select value={form.shop_id} onValueChange={(v) => setForm({ ...form, shop_id: v })}>
              <SelectTrigger data-testid="user-form-shop-select"><SelectValue placeholder="Boutique / Dépôt d'affectation" /></SelectTrigger>
              <SelectContent>{shops.map((s) => <SelectItem key={s.id} value={s.id}>{s.nom}</SelectItem>)}</SelectContent>
            </Select>
            <Input data-testid="user-form-telephone" placeholder="Téléphone (optionnel)" value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} />
            <Input type="email" data-testid="user-form-email" placeholder="Adresse e-mail (pour les notifications)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <Input
              type="number" min="0" max="100" step="0.1"
              data-testid="user-form-commission"
              placeholder="Taux de commission sur ventes (%) — optionnel"
              value={form.commission_percent}
              onChange={(e) => setForm({ ...form, commission_percent: e.target.value })}
            />
            <Input data-testid="user-form-matricule" placeholder="Matricule (7 chiffres)" maxLength={7} value={form.matricule} onChange={(e) => setForm({ ...form, matricule: e.target.value.replace(/\D/g, "").slice(0, 7) })} />
            <Input data-testid="user-form-pin" placeholder={editing ? "Nouveau code PIN (laisser vide pour ne pas changer) — 8 chiffres" : "Code PIN (8 chiffres)"} maxLength={8} value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "").slice(0, 8) })} />

            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Badge de connexion (NFC ou RFID) — optionnel, en plus du matricule + code PIN</p>
              <Select
                value={form.badge_type || "__none__"}
                onValueChange={(v) => setForm({ ...form, badge_type: v === "__none__" ? "" : v, badge_code: v === "__none__" ? "" : form.badge_code })}
              >
                <SelectTrigger data-testid="user-form-badge-type"><SelectValue placeholder="Aucun badge" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Aucun badge</SelectItem>
                  <SelectItem value="nfc">NFC</SelectItem>
                  <SelectItem value="rfid">RFID</SelectItem>
                </SelectContent>
              </Select>
              {form.badge_type && (
                <BadgeScanInput
                  badgeType={form.badge_type}
                  value={form.badge_code}
                  onChange={(code) => setForm({ ...form, badge_code: code })}
                  testIdPrefix="user-form-badge"
                />
              )}
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-2">Grades (multiple possible)</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {ALL_GRADES.map((g) => (
                  <label key={g} className="flex items-center gap-2 text-sm">
                    <Checkbox data-testid={`user-form-grade-${g}`} checked={form.grades.includes(g)} onCheckedChange={() => toggleGrade(g)} />
                    {g}
                  </label>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Les grades définissent les permissions de départ ; ajustez-les finement ensuite via le bouton <Shield size={11} className="inline -mt-0.5" /> Permissions.
              </p>
            </div>
          </div>
          <DialogFooter><Button data-testid="user-form-save" onClick={save}>Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Panneau de permissions détaillé : entièrement généré à partir du
          catalogue backend (constants.PERMISSIONS_CATALOG), pour que chaque
          droit ajouté côté serveur apparaisse automatiquement ici, groupé et
          expliqué, sans coder une case à cocher en dur par droit. */}
      <Dialog open={permOpen} onOpenChange={setPermOpen}>
        <DialogContent data-testid="user-perms-dialog" className="max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Permissions — {permTarget?.prenom} {permTarget?.nom}</DialogTitle>
          </DialogHeader>
          {permTarget && (
            <>
              <div className="flex items-center justify-between shrink-0 -mt-1 mb-1">
                <p className="text-xs text-muted-foreground">
                  Grade(s) : {(permTarget.grades || []).join(", ") || "aucun"}
                </p>
                <button
                  type="button" onClick={recalculerPermissions}
                  className="text-xs text-primary hover:underline shrink-0"
                  data-testid="user-perms-recalculer"
                  title="Efface tout override manuel et repart des seuls droits accordés par le(s) grade(s) actuel(s) — utile si un droit d'un ancien grade est resté accordé par erreur."
                >
                  Réinitialiser aux grades actuels
                </button>
              </div>
              <div className="relative shrink-0">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="pl-9"
                  placeholder="Rechercher une permission (ex : prix, caisse, tâche...)"
                  value={permSearch}
                  onChange={(e) => setPermSearch(e.target.value)}
                  data-testid="perm-search"
                />
              </div>
              <div className="space-y-3 overflow-y-auto pr-1 -mr-1" data-testid="perm-catalog">
                {filteredCatalog.length === 0 && (
                  <p className="text-sm text-muted-foreground italic py-4 text-center">Aucune permission ne correspond à cette recherche.</p>
                )}
                {filteredCatalog.map((mod) => {
                  const Icon = ICONS[mod.icon] || Shield;
                  const fully = isModuleFullyGranted(mod, permTarget);
                  const any = isModuleAnyGranted(mod, permTarget);
                  return (
                    <div key={mod.id} className="rounded-lg border border-border bg-secondary/30 p-4" data-testid={`perm-module-${mod.id}`}>
                      <div className="flex items-start justify-between gap-3 mb-1">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={`shrink-0 w-7 h-7 rounded-md flex items-center justify-center ${any ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                            <Icon size={15} />
                          </span>
                          <p className="font-semibold text-sm truncate">{mod.label}</p>
                        </div>
                        {mod.type === "actions" && mod.actions.length > 1 && (
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              type="button"
                              onClick={() => setModuleAll(mod, true)}
                              disabled={fully}
                              title="Tout autoriser"
                              className="p-1 rounded text-muted-foreground hover:text-emerald-600 hover:bg-emerald-500/10 disabled:opacity-30"
                              data-testid={`perm-module-all-${mod.id}`}
                            >
                              <Check size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={() => setModuleAll(mod, false)}
                              disabled={!any}
                              title="Tout retirer"
                              className="p-1 rounded text-muted-foreground hover:text-red-600 hover:bg-red-500/10 disabled:opacity-30"
                              data-testid={`perm-module-none-${mod.id}`}
                            >
                              <XIcon size={14} />
                            </button>
                          </div>
                        )}
                      </div>
                      {mod.description && <p className="text-xs text-muted-foreground mb-3">{mod.description}</p>}

                      {mod.type === "boolean" ? (
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox
                            checked={!!permTarget.permissions[mod.id]}
                            onCheckedChange={() => togglePerm(mod.id)}
                            data-testid={`perm-${mod.id}`}
                          />
                          {mod.boolean_label}
                        </label>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 mt-2">
                          {mod.actions.map((a) => (
                            <label key={a.key} className="flex items-start gap-2 text-sm cursor-pointer">
                              <Checkbox
                                className="mt-0.5"
                                checked={!!(permTarget.permissions[mod.id] || {})[a.key]}
                                onCheckedChange={() => togglePerm(mod.id, a.key)}
                                data-testid={`perm-${mod.id}-${a.key}`}
                              />
                              <span>
                                {a.label}
                                {a.help && <span className="block text-[11px] text-muted-foreground leading-tight mt-0.5">{a.help}</span>}
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
          <DialogFooter className="shrink-0"><Button data-testid="user-perms-save" onClick={savePerms}>Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  if (embedded) return body;
  return <Layout title="Utilisateurs">{body}</Layout>;
}
